import { Injectable, ErrorHandler, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from '../../shared/services/toast.service';

export interface ApiError {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance: string;
  traceId: string;
  errors?: { [key: string]: string[] };
}

@Injectable({
  providedIn: 'root'
})
export class GlobalErrorHandler implements ErrorHandler {
  private readonly toastService = inject(ToastService);

  handleError(error: any): void {
    if (error instanceof HttpErrorResponse) {
      this.handleHttpError(error);
    } else {
      this.handleGenericError(error);
    }
    
    // Log to console for debugging
    console.error('Global error:', error);
    
    // In production, you might want to send to logging service
    this.logErrorToService(error);
  }

  private handleHttpError(error: HttpErrorResponse): void {
    let errorMessage = 'An error occurred';
    
    switch (error.status) {
      case 0:
        errorMessage = 'Network error - please check your connection';
        break;
      case 400:
        errorMessage = this.extractApiErrorMessage(error) || 'Bad request';
        break;
      case 401:
        errorMessage = this.extractApiErrorMessage(error) || 'Unauthorized - please login again';
        break;
      case 403:
        errorMessage = 'Access forbidden - you don\'t have permission';
        break;
      case 404:
        errorMessage = 'Resource not found';
        break;
      case 409:
        errorMessage = this.extractApiErrorMessage(error) || 'Conflict - resource already exists';
        break;
      case 422:
        errorMessage = this.extractValidationErrors(error) || 'Validation failed';
        break;
      case 500:
        errorMessage = 'Server error - please try again later';
        break;
      case 503:
        errorMessage = 'Service unavailable - please try again later';
        break;
      default:
        errorMessage = `Error ${error.status}: ${error.statusText}`;
    }

    this.toastService.error(errorMessage);
  }

  private handleGenericError(error: Error): void {
    const errorMessage = error.message || 'An unexpected error occurred';
    this.toastService.error(errorMessage);
  }

  private extractApiErrorMessage(error: HttpErrorResponse): string | null {
    if (error.error && typeof error.error === 'object') {
      const apiError = error.error as ApiError;
      return apiError.detail || apiError.title || (error.error as any).message || null;
    }
    return null;
  }

  private extractValidationErrors(error: HttpErrorResponse): string | null {
    if (error.error && typeof error.error === 'object') {
      const apiError = error.error as ApiError;
      if (apiError.errors) {
        const errorMessages = Object.values(apiError.errors).flat();
        return errorMessages.join(', ');
      }
    }
    return null;
  }

  private logErrorToService(error: any): void {
    // TODO: Implement error logging service
    // This could send errors to a logging service like Sentry, LogRocket, etc.
    console.log('Error logged:', {
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });
  }
}

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService {
  private readonly toastService = inject(ToastService);

  handleApiError(error: HttpErrorResponse, context?: string): void {
    const prefix = context ? `${context}: ` : '';
    
    if (error.status === 422 && error.error?.errors) {
      // Handle validation errors
      const validationErrors = this.extractValidationErrors(error.error.errors);
      this.toastService.error(`${prefix}${validationErrors}`);
    } else if (error.error?.detail) {
      this.toastService.error(`${prefix}${error.error.detail}`);
    } else if (error.error?.title) {
      this.toastService.error(`${prefix}${error.error.title}`);
    } else {
      this.toastService.error(`${prefix}Operation failed`);
    }
  }

  private extractValidationErrors(errors: { [key: string]: string[] }): string {
    return Object.entries(errors)
      .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
      .join('; ');
  }

  handleSuccess(message: string): void {
    this.toastService.success(message);
  }

  handleWarning(message: string): void {
    this.toastService.warning(message);
  }

  handleInfo(message: string): void {
    this.toastService.info(message);
  }
}